import { useCallback, useEffect, useRef, useState } from 'react';
import WaveSurfer from 'wavesurfer.js';

type WaveOptions = {
  height?: number;
  waveColor?: string;
  progressColor?: string;
  cursorColor?: string;
};

type WaveState = {
  waveformRef: React.MutableRefObject<HTMLDivElement | null>;
  isReady: boolean;
  isPlaying: boolean;
  error: string | null;
  playPause: () => void;
  setPlaybackRate: (rate: number) => void;
  stop: () => void;
  getCurrentTime: () => number;
  setVolume: (volume: number) => void;
  getDecodedData: () => AudioBuffer | null;
  getDuration: () => number;
  playSegment: (start: number, end?: number) => void;
  on: (event: string, handler: (...args: unknown[]) => void) => void;
  off: (event: string, handler: (...args: unknown[]) => void) => void;
};

const defaultColors = {
  waveColor: 'rgba(27, 27, 27, 0.25)',
  progressColor: 'rgba(27, 27, 27, 0.85)',
  cursorColor: 'rgba(27, 27, 27, 0.9)',
};

export const useWaveSurfer = (
  audioUrl: string | null,
  options?: WaveOptions,
  themeKey?: string
): WaveState => {
  const waveformRef = useRef<HTMLDivElement | null>(null);
  const wavesurferRef = useRef<WaveSurfer | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const playPause = useCallback(() => {
    wavesurferRef.current?.playPause();
  }, []);

  const setPlaybackRate = useCallback((rate: number) => {
    wavesurferRef.current?.setPlaybackRate(rate);
  }, []);

  const stop = useCallback(() => {
    if (wavesurferRef.current?.isPlaying()) {
      wavesurferRef.current.stop();
    }
  }, []);

  const getCurrentTime = useCallback(() => {
    return wavesurferRef.current?.getCurrentTime() ?? 0;
  }, []);

  const setVolume = useCallback((volume: number) => {
    wavesurferRef.current?.setVolume(volume);
  }, []);

  const getDecodedData = useCallback(() => {
    return wavesurferRef.current?.getDecodedData() ?? null;
  }, []);

  const getDuration = useCallback(() => {
    return wavesurferRef.current?.getDuration() ?? 0;
  }, []);

  const playSegment = useCallback((start: number, end?: number) => {
    if (!wavesurferRef.current) {
      return;
    }
    if (typeof end === 'number') {
      wavesurferRef.current.play(start, end);
      return;
    }
    wavesurferRef.current.play(start);
  }, []);

  const on = useCallback((event: string, handler: (...args: unknown[]) => void) => {
    wavesurferRef.current?.on(event as never, handler as never);
  }, []);

  const off = useCallback((event: string, handler: (...args: unknown[]) => void) => {
    wavesurferRef.current?.un(event as never, handler as never);
  }, []);

  useEffect(() => {
    if (!audioUrl) {
      if (wavesurferRef.current) {
        wavesurferRef.current.destroy();
        wavesurferRef.current = null;
      }
      setIsReady(false);
      setIsPlaying(false);
      setError(null);
      return;
    }
    if (!waveformRef.current) {
      return;
    }
    if (wavesurferRef.current) {
      if (wavesurferRef.current.isPlaying()) {
        wavesurferRef.current.stop();
      }
      wavesurferRef.current.destroy();
      wavesurferRef.current = null;
    }
    setIsReady(false);
    setIsPlaying(false);
    setError(null);
    const wave = WaveSurfer.create({
      container: waveformRef.current,
      height: options?.height ?? 120,
      barWidth: 2,
      barGap: 2,
      barRadius: 2,
      normalize: true,
      backend: 'WebAudio',
      interact: true,
      dragToSeek: true,
      cursorWidth: 2,
      waveColor:
        options?.waveColor ??
        (getComputedStyle(document.body).getPropertyValue('--wave').trim() ||
          defaultColors.waveColor),
      progressColor:
        options?.progressColor ??
        (getComputedStyle(document.body).getPropertyValue('--wave-progress').trim() ||
          defaultColors.progressColor),
      cursorColor:
        options?.cursorColor ??
        (getComputedStyle(document.body).getPropertyValue('--wave-cursor').trim() ||
          defaultColors.cursorColor),
    });
    wavesurferRef.current = wave;
    wave.on('ready', () => {
      setIsReady(true);
    });
    wave.on('play', () => {
      setIsPlaying(true);
    });
    wave.on('pause', () => {
      setIsPlaying(false);
    });
    wave.on('finish', () => {
      setIsPlaying(false);
    });
    wave.on('error', (err) => {
      const message = typeof err === 'string' ? err : String(err ?? '');
      if (message.toLowerCase().includes('abort')) {
        return;
      }
      setError('Unable to load waveform.');
    });
    const loadPromise = wave.load(audioUrl);
    if (loadPromise && typeof (loadPromise as Promise<void>).catch === 'function') {
      (loadPromise as Promise<void>).catch((err) => {
        const message = String(err ?? '');
        if (message.toLowerCase().includes('abort')) {
          return;
        }
        setError('Unable to load waveform.');
      });
    }
    return () => {
      if (wave.isPlaying()) {
        wave.stop();
      }
      wave.destroy();
      if (wavesurferRef.current === wave) {
        wavesurferRef.current = null;
      }
    };
  }, [
    audioUrl,
    options?.height,
    options?.waveColor,
    options?.progressColor,
    options?.cursorColor,
    themeKey,
  ]);


  const updateWaveColors = useCallback(() => {
    if (!wavesurferRef.current) {
      return;
    }
    const waveColor =
      options?.waveColor ??
      (getComputedStyle(document.body).getPropertyValue('--wave').trim() ||
        defaultColors.waveColor);
    const progressColor =
      options?.progressColor ??
      (getComputedStyle(document.body)
        .getPropertyValue('--wave-progress')
        .trim() ||
        defaultColors.progressColor);
    const cursorColor =
      options?.cursorColor ??
      (getComputedStyle(document.body).getPropertyValue('--wave-cursor').trim() ||
        defaultColors.cursorColor);
    wavesurferRef.current.setOptions({
      waveColor,
      progressColor,
      cursorColor,
    });
  }, [options?.waveColor, options?.progressColor, options?.cursorColor]);

  useEffect(() => {
    updateWaveColors();
  }, [updateWaveColors, themeKey]);
  return {
    waveformRef,
    isReady,
    isPlaying,
    error,
    playPause,
    setPlaybackRate,
    stop,
    getCurrentTime,
    setVolume,
    getDecodedData,
    getDuration,
    playSegment,
    on,
    off,
  };
};
